import {
  useEffect,
  useRef,
  useState,
  type MouseEvent,
  type RefObject,
} from "react"
import { Place } from "@/types/place"
import debounce from "lodash/debounce"
import { InView } from "react-intersection-observer"

import { useRouter } from "@/lib/router"
import { selectIsXS } from "@/lib/storage/app-slice"
import {
  EntitySheetTrackingContext,
  openEntitySheet,
} from "@/lib/storage/detail-sheet-slice"
import { useAppDispatch, useAppSelector } from "@/lib/storage/hooks"
import { selectIsUserCrawler } from "@/lib/storage/user-slice"
import { isPlaceDetailPage } from "@/lib/utils/routing"
import { CardGrid } from "./card-grid"
import { PlaceCard } from "./place-card"
import { PlacesCarousel } from "./places-carousel"
import { Button } from "./ui/button"
import { useIsInSheet } from "./ui/sheet-context"

export function PlaceCardGrid({
  places,
  rows = 2,
  scrollRef,
  onLoadMore,
  hasMore,
  isLoading,
  showXSCarousel = true,
  entitySheetTrackingContext,
}: {
  places: Place[]
  rows?: number
  scrollRef?: RefObject<HTMLElement | null>
  onLoadMore?: () => void
  hasMore?: boolean
  isLoading?: boolean
  showXSCarousel?: boolean
  entitySheetTrackingContext: EntitySheetTrackingContext
}) {
  const dispatch = useAppDispatch()
  const router = useRouter()
  const isXS = useAppSelector(selectIsXS)
  const isCrawler = useAppSelector(selectIsUserCrawler)
  const sheet = useIsInSheet()

  function handlePlaceClick(e: MouseEvent, place: Place | undefined) {
    if (place) {
      if (!isPlaceDetailPage(router) || sheet) {
        e.preventDefault()
        void dispatch(
          openEntitySheet({
            entity: place,
            entitySheetTrackingContext,
          })
        )
      }
    }
  }

  if (!places || places.length === 0) return null

  return (
    <InView
      triggerOnce
      root={scrollRef?.current}
      disabled={isCrawler}
      rootMargin="50%"
    >
      {({ inView, ref }) => (
        <div ref={ref}>
          {isXS && showXSCarousel ? (
            <PlacesCarousel
              loading={!inView}
              places={places}
              className="[--carousel-outdent-l:var(--sheet-pad)] [--carousel-outdent-r:var(--sheet-pad)]"
              entitySheetTrackingContext={entitySheetTrackingContext}
            />
          ) : (
            <PlacesGridContent
              inView={inView}
              places={places}
              rows={rows}
              onPlaceClick={handlePlaceClick}
              onLoadMore={onLoadMore}
              hasMore={hasMore}
              isLoading={isLoading}
            />
          )}
        </div>
      )}
    </InView>
  )
}

function PlacesGridContent({
  inView,
  places,
  rows,
  onPlaceClick,
  onLoadMore,
  hasMore,
  isLoading,
}: {
  inView: boolean
  places: Place[]
  rows: number
  onPlaceClick: (e: MouseEvent, place: Place | undefined) => void
  onLoadMore?: () => void
  hasMore?: boolean
  isLoading?: boolean
}) {
  const gridRef = useRef<HTMLDivElement>(null)
  const [showMore, setShowMore] = useState(false)
  const [initialCardsToShow, setInitialCardsToShow] = useState(6)
  const [cardsToShow, setCardsToShow] = useState(initialCardsToShow)

  useEffect(() => {
    const grid = gridRef.current
    if (!grid) return

    const handleResize = () => {
      const firstItem = grid.querySelector("li") as HTMLElement
      if (firstItem) {
        const itemWidth = firstItem.clientWidth
        const gridWidth = grid.clientWidth
        const itemsPerRow = Math.floor(gridWidth / itemWidth)
        const newInitialCardsToShow = itemsPerRow * rows
        setInitialCardsToShow(newInitialCardsToShow)
        setCardsToShow(showMore ? places.length : newInitialCardsToShow)
      }
    }

    const resizeObserver = new ResizeObserver(debounce(handleResize, 300))
    resizeObserver.observe(grid)

    handleResize()

    return () => resizeObserver.disconnect()
  }, [places.length, rows, showMore])

  useEffect(() => {
    if (showMore) {
      setCardsToShow(places.length)
    }
  }, [places.length, showMore])

  return (
    <>
      <CardGrid ref={gridRef}>
        {places.slice(0, cardsToShow).map((place) => (
          <li key={place.entity_ref}>
            <PlaceCard
              place={inView ? place : undefined}
              onClick={(e) => onPlaceClick(e, place)}
              trackingContext="CardGrid"
            />
          </li>
        ))}
      </CardGrid>
      {!showMore && places.length > initialCardsToShow && (
        <div className="mt-10 flex justify-center">
          <Button
            size="md"
            variant="outline"
            loading={isLoading}
            onClick={() => {
              setShowMore(true)
              setCardsToShow(places.length)
              if (onLoadMore && hasMore) {
                void onLoadMore()
              }
            }}
          >
            Show more
          </Button>
        </div>
      )}
      {(showMore || places.length <= initialCardsToShow) &&
        onLoadMore &&
        hasMore && (
          <div className="mt-10 flex justify-center">
            <Button
              size="md"
              variant="outline"
              loading={isLoading}
              onClick={() => {
                setShowMore(true)
                void onLoadMore()
              }}
            >
              Show more
            </Button>
          </div>
        )}
    </>
  )
}
